import { Navigate, useNavigate } from 'react-router-dom';
import './Profile.css';
import UserSettings from '../components/profile/views/UserSettings';
import AdminSettings from '../components/profile/views/AdminSettings';
import { useEffect, useState } from 'react';
import AdminAccessSettings from '../components/profile/views/AdminAccessSettings';
import { PromptVersionControl } from '@Models/types';
import TokenAnalytics from '@Profile/views/AdminTokenSettings';
import {
  Brain,
  ClipboardText,
  Coins,
  Envelope,
  Globe,
  HeadCircuit,
  LightbulbFilament,
  User,
} from '@phosphor-icons/react';
import APISettings from '@Profile/views/APISettings';
import AISettings from '@Profile/views/AISettings';

import { useProfileContext } from '@Context/ProfileContext';
import { useAuth } from '@Context/CognitoAuthProvider';
import { accountsAPI } from '@Lib/axios/account';
import JiraAnalyzerReportsSettings from '@Profile/views/JiraAnalyzerReportsSettings';
import ArtilleryEmailSettings from '@Profile/views/ArtilleryEmailSettings';
import TabPanel from '@Generator/tabs/TabPanel';
import Tab from '@Generator/tabs/Tab';
import Tabs from '@Generator/tabs/Tabs';

function Profile() {
  const navigate = useNavigate();
  const { setTestRailCreds, setJiraCreds } = useProfileContext();
  const [prompts, setPrompts] = useState<PromptVersionControl[] | null>(null);
  const [users, setUsers] = useState<string[]>([]);

  const { user, logout } = useAuth();
  const account = accountsAPI(logout);

  const isAdmin = user?.admin;

  useEffect(() => {
    if (user) {
      if (!user.profile) {
        navigate('/home');
      } else {
        setTestRailCreds({
          testrail_username: user.profile.testrail_username,
          testrail_api_key: user.profile.testrail_api_key,
        });
        setJiraCreds({
          // domain: user.profile.jira_domain || '',
          email: user.profile.jira_email || '',
          token: user.profile.jira_api_key || '',
        });
      }
    }
  }, [user]);

  const [usersLoading, setUsersLoading] = useState(true);

  useEffect(() => {
    if (user && isAdmin) {
      setUsersLoading(true);
      account
        .getAllUsers()
        .then((data) => setUsers(data))
        .catch((error) => {
          console.log(error);
        })
        .finally(() => setUsersLoading(false));

      account
        .getAllPrompts()
        .then((data) => {
          setPrompts(data);
        })
        .catch((error) => {
          console.log(error);
        });
    }
  }, [user]);

  if (!user) {
    return <Navigate to="/" />;
  }

  return (
    <div className="flex h-[100vh] flex-col">
      <Tabs>
        <div
          id="tab-list-profile"
          className="flex flex-col items-center gap-y-2 rounded-xl bg-neutral-200 p-4 sm:flex-row sm:justify-between"
        >
          <div className="flex flex-row">
            <Tab index={0} label="API" icon={<Brain size={25} />} />
            <Tab
              index={1}
              label="Prompt"
              icon={<LightbulbFilament size={25} />}
            />
            <Tab index={2} label="AI" icon={<HeadCircuit size={25} />} />
            {isAdmin && (
              <>
                <Tab
                  index={3}
                  label="Global Prompt"
                  icon={<Globe size={25} />}
                />
                <Tab index={4} label="User" icon={<User size={25} />} />
                <Tab index={5} label="Tokens" icon={<Coins size={25} />} />
                <Tab
                  index={6}
                  label="Reports"
                  icon={<ClipboardText size={25} />}
                />
                <Tab
                  index={7}
                  label="Artillery Emails"
                  icon={<Envelope size={25} />}
                />
              </>
            )}
          </div>
        </div>
        <TabPanel index={0}>
          <APISettings />
        </TabPanel>

        <TabPanel index={1}>
          {user.profile && <UserSettings profile={user.profile} />}
        </TabPanel>

        <TabPanel index={2}>
          <AISettings />
        </TabPanel>

        {user.profile && isAdmin && (
          <>
            <TabPanel index={3}>
              <AdminSettings profile={user.profile} prompts={prompts || []} />
            </TabPanel>

            <TabPanel index={4}>
              <AdminAccessSettings users={users} loading={usersLoading} />
            </TabPanel>

            <TabPanel index={5}>
              <TokenAnalytics />
            </TabPanel>

            <TabPanel index={6}>
              <JiraAnalyzerReportsSettings />
            </TabPanel>

            <TabPanel index={7}>
              <ArtilleryEmailSettings />
            </TabPanel>
          </>
        )}
      </Tabs>
    </div>
  );
}
export default Profile;
